<template>
  <div v-if="visible" class="batch-create-modal-overlay" @click="closeModal">
    <div class="batch-create-modal" @click.stop>
      <div class="modal-header">
        <h3>批量创建</h3>
        <button class="close-btn" @click="closeModal">
          <i class="el-icon-close"></i>
        </button>
      </div>
      <div class="modal-content">
        <!-- 顶部信息栏 -->
        <div class="top-info-bar">
          <div class="user-info">
            <span class="label">选中用户：</span>
            <span class="value">{{ selectedUserIds.length }} 个</span>
          </div>

          <!-- 内容类型选择 -->
          <div class="type-options">
            <div
              :class="['type-option', { active: selectedType === 'message' }]"
              @click="selectContentType('message')">
              <div class="type-icon">
                <i class="el-icon-chat-dot-round"></i>
              </div>
              <div class="type-info">
                <div class="type-name">消息</div>
                <div class="type-desc">最多300字符</div>
              </div>
            </div>
            <div
              :class="['type-option', { active: selectedType === 'email' }]"
              @click="selectContentType('email')">
              <div class="type-icon">
                <i class="el-icon-message"></i>
              </div>
              <div class="type-info">
                <div class="type-name">邮件</div>
                <div class="type-desc">最少150字符，最多3500字符</div>
              </div>
            </div>
          </div>
        </div>

        <div class="message-section">
          <h4 class="section-title">
            <i class="el-icon-edit"></i>
            {{ contentTitle }}
          </h4>

          <!-- 批量添加按钮 -->
          <div class="batch-add-section">
            <button class="batch-add-btn" @click="showBatchModal = true">
              <i class="el-icon-plus"></i>
              批量添加
            </button>
          </div>

          <!-- 批量添加弹窗 -->
          <div
            v-if="showBatchModal"
            class="batch-modal-overlay"
            @click="closeBatchModal">
            <div class="batch-modal" @click.stop>
              <div class="batch-modal-header">
                <h4>批量添加内容</h4>
                <button class="batch-modal-close-btn" @click="closeBatchModal">
                  <i class="el-icon-close"></i>
                </button>
              </div>
              <div class="batch-modal-content">
                <div class="batch-modal-description">
                  请输入内容，每行一条{{
                    selectedType === 'message' ? '消息' : '邮件'
                  }}
                </div>
                <div class="batch-textarea-wrapper">
                  <textarea
                    v-model="batchText"
                    class="batch-modal-textarea"
                    placeholder=""
                    rows="12"></textarea>
                </div>
                <div class="batch-modal-info">
                  <span>当前已添加 {{ messageList.length }} 条内容</span>
                  <span v-if="batchText.trim()"
                    >将添加 {{ getBatchMessageCount() }} 条新内容</span
                  >
                </div>
              </div>
              <div class="batch-modal-actions">
                <button class="batch-modal-cancel-btn" @click="closeBatchModal">
                  取消
                </button>
                <button
                  class="batch-modal-confirm-btn"
                  @click="processBatchText"
                  :disabled="!batchText.trim()">
                  确定添加
                </button>
              </div>
            </div>
          </div>

          <div class="message-list">
            <!-- 已添加的消息 -->
            <div
              v-for="(message, index) in messageList"
              :key="index"
              class="message-item">
              <div class="message-index">{{ index + 1 }}</div>
              <div class="input-wrapper">
                <input
                  v-model="messageList[index]"
                  class="message-input"
                  :maxlength="maxLength"
                  @blur="saveMessage(index)" />
                <div class="char-count">
                  {{ messageList[index].length }}/{{ maxLength }}
                </div>
              </div>
              <button class="delete-btn" @click="removeMessage(index)">
                <i class="el-icon-close"></i>
              </button>
            </div>

            <!-- 正在输入的消息 -->
            <div class="message-item input-item">
              <div class="message-index">{{ messageList.length + 1 }}</div>
              <div class="input-wrapper">
                <input
                  v-model="currentMessage"
                  class="message-input"
                  :placeholder="inputPlaceholder"
                  @keyup.enter="addMessage"
                  :maxlength="maxLength" />
                <div class="char-count">
                  {{ currentMessage.length }}/{{ maxLength }}
                </div>
              </div>
              <button
                class="add-btn"
                @click="addMessage"
                :disabled="!canAddMessage">
                <i class="el-icon-check"></i>
              </button>
            </div>
          </div>
          <div class="message-count">
            共 {{ messageList.length }} 条{{
              selectedType === 'message' ? '消息' : '邮件'
            }}
          </div>
        </div>

        <div class="modal-actions">
          <button class="cancel-btn" @click="closeModal">取消</button>
          <button
            class="confirm-btn"
            @click="handleSubmit"
            :disabled="isSubmitting">
            <span v-if="isSubmitting">创建中...</span>
            <span v-else>确认创建</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { batchAddTaskTemplate } from '@/api/talkytimes/masSending';

export default {
  name: 'BatchCreateModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    selectedUserIds: {
      type: Array,
      default: () => [],
    },
    activeTab: {
      type: String,
      default: 'message',
    },
  },
  data() {
    return {
      currentMessage: '',
      messageList: [],
      isSubmitting: false,
      selectedType: 'message',
      showBatchModal: false,
      batchText: '',
    };
  },
  computed: {
    contentTitle() {
      return this.selectedType === 'message' ? '消息内容' : '邮件内容';
    },
    inputPlaceholder() {
      return this.selectedType === 'message'
        ? '请输入消息内容，按回车添加...'
        : '请输入邮件内容，按回车添加...（最少150字符）';
    },
    maxLength() {
      return this.selectedType === 'message' ? 300 : 3500; // 两种类型都是1500字符
    },
    canAddMessage() {
      if (!this.currentMessage.trim()) return false;

      // 邮件类型需要至少150个字符
      if (this.selectedType === 'email' && this.currentMessage.length < 150) {
        return false;
      }

      return true;
    },
  },
  methods: {
    closeModal() {
      this.$emit('close');
      this.resetForm();
    },

    resetForm() {
      this.currentMessage = '';
      this.messageList = [];
      this.isSubmitting = false;
      this.selectedType = 'message';
      this.showBatchModal = false;
      this.batchText = '';
    },

    addMessage() {
      const message = this.currentMessage.trim();
      if (message) {
        this.messageList.push(message);
        this.currentMessage = '';
      }
    },

    removeMessage(index) {
      this.messageList.splice(index, 1);
    },

    saveMessage(index) {
      if (this.messageList[index] && this.messageList[index].trim()) {
        this.messageList[index] = this.messageList[index].trim();
      }
    },

    selectContentType(type) {
      this.selectedType = type;
      this.messageList = [];
      this.currentMessage = '';
    },

    closeBatchModal() {
      this.showBatchModal = false;
      this.batchText = '';
    },

    processBatchText() {
      const messages = this.batchText
        .split('\n')
        .map((msg) => msg.trim())
        .filter((msg) => msg);
      this.messageList.push(...messages);
      this.closeBatchModal();
    },

    getBatchMessageCount() {
      const messages = this.batchText
        .split('\n')
        .map((msg) => msg.trim())
        .filter((msg) => msg);
      return messages.length;
    },

    async handleSubmit() {
      if (this.messageList.length === 0) {
        this.$message.warning('请输入至少一条内容');
        return;
      }

      // 验证邮件类型的最少字符数
      if (this.selectedType === 'email') {
        const invalidMessages = this.messageList.filter(
          (msg) => msg.length < 150
        );
        if (invalidMessages.length > 0) {
          this.$message.warning('邮件内容每条至少需要150个字符');
          return;
        }
      }

      this.isSubmitting = true;

      try {
        // 使用批量创建接口

        const batchData = this.selectedUserIds.map((userId) => ({
          idUser: userId,
          jobType: this.selectedType === 'message' ? '1' : '0', // 1=消息, 0=邮件
          messageContentList: [],
        }));
        // 计算每个用户分配的消息数量
        const messagesPerUser = Math.floor(
          this.messageList.length / batchData.length
        );
        const remainingMessages = this.messageList.length % batchData.length;

        let messageIndex = 0;
        for (let i = 0; i < batchData.length; i++) {
          // 计算当前用户应该分配的消息数量
          const currentUserMessageCount =
            messagesPerUser + (i < remainingMessages ? 1 : 0);

          // 为当前用户分配连续的消息
          for (let j = 0; j < currentUserMessageCount; j++) {
            if (messageIndex < this.messageList.length) {
              batchData[i].messageContentList.push(
                this.messageList[messageIndex]
              );
              messageIndex++;
            }
          }
        }
        await batchAddTaskTemplate(batchData);

        const typeText = this.selectedType === 'message' ? '消息' : '邮件';
        this.$emit(
          'success',
          `已成功为 ${this.selectedUserIds.length} 个用户创建 ${this.messageList.length} 条${typeText}模板`
        );
        this.closeModal();
      } catch (error) {
        console.error('批量创建失败:', error);
        this.$emit('error', '批量创建失败，请重试');
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.batch-create-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.batch-create-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 95vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.9);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(229, 231, 235, 0.8);
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border-radius: 6px;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  transform: scale(1.1);
}

.modal-content {
  padding: 24px;
  max-height: calc(95vh - 80px);
  overflow-y: auto;
}

.top-info-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: rgba(16, 185, 129, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(16, 185, 129, 0.1);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-info .label {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.user-info .value {
  font-size: 14px;
  color: #1f2937;
  font-weight: 600;
}

.type-options {
  display: flex;
  gap: 8px;
}

.type-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 2px solid rgba(229, 231, 235, 0.8);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(249, 250, 251, 0.8);
  min-width: 140px;
}

.type-option:hover {
  border-color: #10b981;
  background: rgba(240, 247, 255, 0.8);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.15);
}

.type-option.active {
  border-color: #10b981;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3);
}

.type-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(16, 185, 129, 0.1);
  border-radius: 4px;
  color: #10b981;
  transition: all 0.3s ease;
  font-size: 14px;
}

.type-option.active .type-icon {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.type-info {
  flex: 1;
}

.type-name {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 1px;
}

.type-desc {
  font-size: 11px;
  color: #6b7280;
  line-height: 1.2;
}

.type-option.active .type-desc {
  color: rgba(255, 255, 255, 0.8);
}

.message-section {
  margin-bottom: 24px;
  margin-top: 8px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  padding-bottom: 8px;
  border-bottom: 2px solid rgba(16, 185, 129, 0.2);
}

.section-title i {
  color: #10b981;
  font-size: 18px;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(229, 231, 235, 0.8);
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  background: white;
  transition: all 0.3s ease;
}

.form-input:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.form-textarea {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(229, 231, 235, 0.8);
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  background: white;
  resize: vertical;
  min-height: 120px;
  font-family: inherit;
  transition: all 0.3s ease;
}

.form-textarea:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.interval-input {
  display: flex;
  align-items: center;
  gap: 8px;
}

.interval-input .form-input {
  flex: 1;
}

.unit-text {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
  white-space: nowrap;
}

.input-wrapper {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
}

.message-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid rgba(229, 231, 235, 0.8);
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  background: white;
  outline: none;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.message-input:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.1);
}

.char-count {
  position: absolute;
  right: 8px;
  top: 8px;
  font-size: 11px;
  color: #9ca3af;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 6px;
  border-radius: 4px;
  pointer-events: none;
  font-weight: 500;
}

.add-btn {
  width: 24px;
  height: 24px;
  border: 1px solid rgba(16, 185, 129, 0.3);
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  margin-left: 8px;
  flex-shrink: 0;
}

.add-btn:hover:not(:disabled) {
  background: rgba(16, 185, 129, 0.2);
  border-color: #10b981;
}

.add-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.message-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.message-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.message-index {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.delete-btn {
  width: 24px;
  height: 24px;
  border: 1px solid rgba(239, 68, 68, 0.3);
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  margin-left: 8px;
  flex-shrink: 0;
}

.delete-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  transform: scale(1.1);
}

.message-count {
  margin-top: 8px;
  font-size: 12px;
  color: #10b981;
  font-weight: 500;
}

.batch-add-section {
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-start;
}

.batch-add-btn {
  padding: 8px 16px;
  border: 1px solid rgba(16, 185, 129, 0.3);
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.batch-add-btn:hover:not(:disabled) {
  background: rgba(16, 185, 129, 0.2);
  border-color: #10b981;
}

.batch-add-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.batch-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  /* Ensure it's above other content */
  backdrop-filter: blur(5px);
}

.batch-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
  display: flex;
  flex-direction: column;
}

.batch-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(229, 231, 235, 0.8);
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.batch-modal-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.batch-modal-close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border-radius: 6px;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.batch-modal-close-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  transform: scale(1.1);
}

.batch-modal-content {
  padding: 24px;
  max-height: calc(85vh - 100px);
  /* Adjust for header and footer */
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.batch-modal-description {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px dashed #e5e7eb;
}

.batch-textarea-wrapper {
  position: relative;
}

.batch-modal-textarea {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(229, 231, 235, 0.8);
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  background: white;
  resize: vertical;
  min-height: 120px;
  font-family: inherit;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.batch-modal-textarea:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.batch-modal-info {
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  font-size: 12px;
  color: #6b7280;
}

.batch-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding: 16px;
  border-top: 1px solid rgba(229, 231, 235, 0.8);
}

.batch-modal-cancel-btn,
.batch-modal-confirm-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.batch-modal-cancel-btn {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
}

.batch-modal-cancel-btn:hover {
  background: rgba(107, 114, 128, 0.2);
  transform: translateY(-1px);
}

.batch-modal-confirm-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.batch-modal-confirm-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
  transform: translateY(-1px);
}

.batch-modal-confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.modal-actions {
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(229, 231, 235, 0.8);
}

.cancel-btn,
.confirm-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 100px;
}

.cancel-btn {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
}

.cancel-btn:hover {
  background: rgba(107, 114, 128, 0.2);
  transform: translateY(-1px);
}

.confirm-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.confirm-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
  transform: translateY(-1px);
}

.confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}
</style>
